import { GetTokenSilentlyOptions } from "@auth0/auth0-react";

export interface ApiClientConfig {
  baseUrl: string;
  getAccessToken?: () => Promise<string>;
  onUnauthorized?: () => void;
  isGuest?: boolean;
}

export interface ApiError {
  status: number;
  message: string;
  code?: string;
  details?: unknown;
  apiResponse?: unknown;
}

export type ApiResult<T> =
  | { success: true; data: T }
  | { success: false; error: ApiError };

export type GetAccessTokenSilentlyType = (
  options?: GetTokenSilentlyOptions,
) => Promise<string>;

export interface PaginationParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BaseServiceInterface {
  // Note: handleRequest is protected in implementation
}

/**
 * Response wrapper types for common backend patterns
 */
export interface CountResponse {
  count: number;
}

export interface DataResponse<T> {
  data: T;
}

/**
 * Utility functions for transforming API responses
 * DRY improvement: Eliminates repeated count extraction pattern across services
 */

/**
 * Extracts count from a wrapped { count: number } response
 * @example
 * const result = await this.handleRequest<CountResponse>(axios.get('/api/count'));
 * return extractCount(result);
 */
export function extractCount(
  result: ApiResult<CountResponse>
): ApiResult<number> {
  if (result.success) {
    return { success: true, data: result.data.count };
  }
  return result as ApiResult<number>;
}

/**
 * Extracts data from a wrapped { data: T } response
 * @example
 * const result = await this.handleRequest<DataResponse<MyType>>(axios.get('/api/data'));
 * return extractData(result);
 */
export function extractData<T>(
  result: ApiResult<DataResponse<T>>
): ApiResult<T> {
  if (result.success) {
    return { success: true, data: result.data.data };
  }
  return result as ApiResult<T>;
}

/**
 * Generic extractor for any wrapped response with a known property
 * @example
 * const result = await this.handleRequest<{ items: Item[] }>(axios.get('/api/items'));
 * return extractProperty(result, 'items');
 */
export function extractProperty<TResponse, TKey extends keyof TResponse>(
  result: ApiResult<TResponse>,
  key: TKey
): ApiResult<TResponse[TKey]> {
  if (result.success) {
    return { success: true, data: result.data[key] };
  }
  return result as ApiResult<TResponse[TKey]>;
}
